import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Pebble } from "../../../../components/Pebble";
import styles from "./MarketGroup.module.scss";
import {
  MARKETS_VIEW,
  ODDS_VIEW,
  ODDS_FILTER_OPTIONS,
} from "../../../../constants";
import {
  selectOddsAndUpdateFavourites,
  removeOddsAndUpdateFavourites,
} from "../../../../actions/favourites";
import {
  ADD,
  ALL,
  DESELECT,
  DESELECT_ALL,
  FAVOURITES,
  SELECT_ALL,
} from "../../../../constants/analytics";
import { sendAnalyticsEvent } from "../../../../lib/postMessage";

export const MarketGroup = ({ subHeader = "", options, type }) => {
  const dispatch = useDispatch();
  const { activeView } = useSelector((state) => state.view);
  const { odds, markets } = useSelector((state) => state.favourites);
  const group = type === ODDS_VIEW ? odds : markets;
  const filteredOptions = options.filter((option) => {
    // option.active is undefined for odds
    if (option.active === undefined) return true;
    return option.active;
  });

  function isSelected(option) {
    if (type === ODDS_VIEW) {
      const { minOdds, maxOdds } = odds;
      if (minOdds === null && maxOdds === null) {
        return false;
      }
      return option.minOdds >= minOdds && option.maxOdds <= maxOdds;
    }
    return group.includes(option.type);
  }

  const isGroupSelected =
    type === MARKETS_VIEW
      ? filteredOptions.every((option) => isSelected(option))
      : false;

  function handleSelectAll() {
    if (isGroupSelected) {
      dispatch({
        type: "REMOVE_MARKET_GROUP_FROM_FAVOURITES",
        payload: {
          filter: type,
          selection: filteredOptions,
        },
      });
      sendAnalyticsEvent({
        event: FAVOURITES,
        type: ALL,
        action: DESELECT_ALL,
        value: subHeader,
      });
    } else {
      dispatch({
        type: "ADD_FAVOURITE_MARKET_SELECTION",
        payload: {
          filter: type,
          selection: filteredOptions,
        },
      });
      sendAnalyticsEvent({
        event: FAVOURITES,
        type: ALL,
        action: SELECT_ALL,
        value: subHeader,
      });
    }
  }

  function handleSelect(option) {
    if (type === ODDS_VIEW) {
      const { minOdds, maxOdds } = odds;

      // Helper functions for cleaner code
      const dispatchOddsUpdate = ({ minOdds, maxOdds, action, label }) => {
        dispatch(
          selectOddsAndUpdateFavourites({
            minOdds,
            maxOdds,
            isDecimal: true,
          })
        );

        sendAnalyticsEvent({
          event: FAVOURITES,
          type: ALL,
          action,
          value: label,
        });
      };

      const findOptionIndex = (matchFn) =>
        ODDS_FILTER_OPTIONS.findIndex(matchFn);

      const getOptionByIndex = (index) => ODDS_FILTER_OPTIONS[index];

      // No current selection - select the clicked option
      if (minOdds === null && maxOdds === null) {
        dispatchOddsUpdate({
          minOdds: option.minOdds,
          maxOdds: option.maxOdds,
          action: ADD,
          label: option.label,
        });
        return;
      }

      // Check if option is already selected
      const isOptionSelected = isSelected(option);

      if (isOptionSelected) {
        // Single range selected - remove selection
        if (minOdds === option.minOdds && maxOdds === option.maxOdds) {
          dispatch(removeOddsAndUpdateFavourites(activeView, option.label));
          return;
        }

        // Multiple ranges selected - handle edge removals
        const removeMinEdge = minOdds === option.minOdds;
        const removeMaxEdge = maxOdds === option.maxOdds;
        const removeFromMiddle =
          minOdds < option.minOdds && maxOdds > option.maxOdds;

        if (removeMinEdge) {
          const minIndex = findOptionIndex((o) => o.minOdds === minOdds);
          const nextOption = getOptionByIndex(minIndex + 1);
          dispatchOddsUpdate({
            minOdds: nextOption.minOdds,
            maxOdds,
            action: DESELECT,
            label: option.label,
          });
          return;
        }

        if (removeMaxEdge) {
          const maxIndex = findOptionIndex((o) => o.maxOdds === maxOdds);
          const prevOption = getOptionByIndex(maxIndex - 1);
          dispatchOddsUpdate({
            minOdds,
            maxOdds: prevOption.maxOdds,
            action: DESELECT,
            label: option.label,
          });
          return;
        }

        if (removeFromMiddle) {
          const currentIndex = findOptionIndex(
            (o) => o.minOdds === option.minOdds || o.maxOdds === option.maxOdds
          );

          // removing second-to-last option when last option is Infinity
          // to ensure there is always a continuous range
          const isSecondToLast =
            currentIndex === ODDS_FILTER_OPTIONS.length - 2;
          const isLastOptionInfinity =
            odds.maxOdds === Infinity && odds.minOdds === null;

          if (isSecondToLast && isLastOptionInfinity) {
            const prevOption = getOptionByIndex(currentIndex - 1);
            dispatchOddsUpdate({
              minOdds,
              maxOdds: prevOption.maxOdds,
              action: DESELECT,
              label: option.label,
            });
            return;
          }

          const nextOption = getOptionByIndex(currentIndex + 1);
          dispatchOddsUpdate({
            minOdds: nextOption.minOdds,
            maxOdds,
            action: DESELECT,
            label: option.label,
          });
          return;
        }
      }

      // Option is not selected - expand range to include it
      const newMinOdds = option.minOdds < minOdds ? option.minOdds : minOdds;
      const newMaxOdds = option.maxOdds > maxOdds ? option.maxOdds : maxOdds;

      dispatchOddsUpdate({
        minOdds: newMinOdds,
        maxOdds: newMaxOdds,
        action: ADD,
        label: option.label,
      });
    } else {
      if (isSelected(option)) {
        dispatch({
          type: "REMOVE_MARKET_FROM_FAVOURITES",
          payload: {
            filter: type,
            selection: option,
          },
        });

        sendAnalyticsEvent({
          event: FAVOURITES,
          type: ALL,
          action: DESELECT,
          value: option.label ?? option.name,
        });
      } else {
        dispatch({
          type: "ADD_FAVOURITE_MARKET_SELECTION",
          payload: {
            filter: type,
            selection: option,
          },
        });

        sendAnalyticsEvent({
          event: FAVOURITES,
          type: ALL,
          action: ADD,
          value: option.label ?? option.name,
        });
      }
    }
  }

  return (
    <div className={styles.marketGroup}>
      {subHeader !== "" && (
        <div className={styles.subHeaderContainer}>
          <h4 className={styles.subHeader}>{subHeader}</h4>
          <button
            type="button"
            className={styles.subHeaderButton}
            onClick={handleSelectAll}
          >
            {isGroupSelected ? "Deselect All" : "Select All"}
          </button>
        </div>
      )}
      <div className={styles.options}>
        {filteredOptions.map((option) => (
          <Pebble
            key={option.label ?? option.name}
            label={option.label ?? option.name}
            active={isSelected(option)}
            onClick={() => handleSelect(option)}
          />
        ))}
      </div>
    </div>
  );
};

MarketGroup.propTypes = {
  subHeader: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.string,
      active: PropTypes.bool,
    })
  ).isRequired,
  type: PropTypes.string.isRequired,
};
